import { isActivateTokensAction, isConsumeTokensAction, isExpireTokensAction, isInsertTokensAction } from "@custom-types/credit-type-guards";
import { CREDITS_DISPLAY_NAME } from "@src/constants/credit-constants";
import { capitalizeFirstLetter } from "@utils/string-utils";
const CREDIT_HEADER = capitalizeFirstLetter(CREDITS_DISPLAY_NAME);

/** All the possible headers for transactions table. */
export const TransactionsHeaders = {
  /** The date of each transactions */
  date: "Date",
  /** User who used each of the transactions */
  user: "Users",
  /** Description of the transactions */
  description: "Description",
  /** Project from where the transactions created */
  project: "Project",
  /** Amount of credit information in each transactions */
  credits: CREDIT_HEADER
};
/** All the possible headers for token(credit) history table. */
export const CreditPurchasesHistoryHeaders = {
  /** The name of the package based on the credit amount */
  package: "Package",
  /** The date when the package was purchased */
  purchasedDate: "Purchased date",
  /** The expiration date of the package */
  expirationDate: "Expiration date",
  /** The status of the package */
  status: "Status",
  /** The remaining credits in the package */
  creditsRemaining: `${CREDIT_HEADER} remaining`
};
/** @returns Description for the transaction based on the transaction action */
export function getTransactionDescription(transaction) {
  const tokensCapitalized = capitalizeFirstLetter(CREDITS_DISPLAY_NAME);
  switch (transaction.result.type) {
    case "TokensInserted":
      return `${tokensCapitalized} are added to the workspace balance`;
    case "TokenInsertionFailed":
      return `${tokensCapitalized} addition is failed because of ${transaction.result.data.failureReason}`;
    case "TokenInsertionRejected":
      return `${tokensCapitalized} addition is rejected because of ${transaction.result.data.rejectionReason}`;
    case "TokensActivated":
      return `${tokensCapitalized} are activated and added to the workspace balance`;
    case "TokensCreated":
      return `${tokensCapitalized} subscription has been created`;
    case "TokenConsumptionFailed":
      return `${tokensCapitalized} consumption is failed because of ${transaction.result.data.failureReason}`;
    case "TokenConsumptionRejected":
      return `${tokensCapitalized} consumption is rejected because of ${transaction.result.data.rejectionReason}`;
    case "TokensConsumed":
      if (isConsumeTokensAction(transaction.action)) {
        return `${tokensCapitalized} spent ${transaction.action.data.consumptionReason ? `on ${transaction.action.data.consumptionReason}` : ""}.`;
      }
      return "";
    case "TokensExpired":
      return `${tokensCapitalized} are expired`;
    default:
      return "";
  }
}

/**
 * Determines if transaction is about to be consumed tokens successfully
 * @param transaction The transaction data
 * @returns True if consumed successfully
 */
export function isConsumedTransaction(transaction) {
  return isConsumeTokensAction(transaction.action) && transaction.result.type === "TokensConsumed";
}

/**
 * Determines if transaction is about to be a balance increase
 * @param transaction The transaction data
 * @returns True if the result type is TokensInserted or TokensActivated
 */
export function shouldShowIncrement(transaction) {
  return isInsertTokensAction(transaction.action) && transaction.result.type === "TokensInserted" || isActivateTokensAction(transaction.action) && transaction.result.type === "TokensActivated";
}

/**
 * Determines if transaction is about expired tokens
 * @param transaction The transaction data
 * @returns True if transaction is about expired tokens
 */
export function isExpiredTransaction(transaction) {
  return isExpireTokensAction(transaction.action) && transaction.result.type === "TokensExpired" && typeof transaction.result.data.expiredAmount === "number";
}

/**
 * Determines if transaction is about failed or rejected in terms of insertion or consumption
 * @param transaction The transaction data
 * @returns True if transaction is failed or rejected type
 */
export function isFailedOrRejectedTransaction(transaction) {
  const failedOrRejectedTypes = ["TokenConsumptionFailed", "TokenConsumptionRejected", "TokenInsertionFailed", "TokenInsertionRejected"];
  return failedOrRejectedTypes.includes(transaction.result.type);
}

/**
 * Determines the token amount depending on the transaction type
 * For expired transactions amount is stored as expiredAmount in result
 * For other types it's tokenAmount in action data
 *
 * @param transaction The transaction data
 * @returns Transaction amount
 */
export function getTransactionAmount(transaction) {
  return isExpiredTransaction(transaction) ? transaction.result.data.expiredAmount : transaction.action.data.tokenAmount;
}

/**
 * Determines the symbol for transaction
 *
 * @param transaction The transaction data
 * @returns Symbol depending on the transaction type
 */
export function getAmountSymbol(transaction) {
  if (shouldShowIncrement(transaction)) {
    return "+";
  }
  if (isConsumedTransaction(transaction) || isExpiredTransaction(transaction)) {
    return "-";
  }
  return "";
}